import React from 'react';
import { Box, Button, Stack, alpha } from "@mui/material";
import { GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LockIcon from '@mui/icons-material/Lock';
import Row from "./BetRow";
import { IParlayOutcome, IShopOutcome } from "../pick6/store/types";
import { evAgeLabel } from "../pick6/optimizerHelper";

export const additionalLegsColumns = (getType: (o: IShopOutcome) => 'Add' | 'Swap' | 'Remove', handleLegClick: (o: IParlayOutcome) => void, isDisplayUnlocked: boolean, isPickemTable: boolean): GridColDef[] => ([
  {
    field: 'name',
    headerName: 'Player',
    flex: .3,
    headerAlign: 'left',
    sortable: false,
    renderCell(params: GridRenderCellParams<IShopOutcome>) {
      const { eventLabel, participantLabel } = params.row;
      return (
        <Stack direction='column' overflow='hidden'>
          <Box fontSize='16px' fontWeight={700} sx={{ ['text-wrap']: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{participantLabel}</Box>
          <Box sx={[
            (theme) => ({
              color: alpha(theme.palette.common.white, 0.87),
              paddingTop: '6px',
              ['text-wrap']: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            })
          ]} fontSize='12px' fontWeight={400}>{eventLabel}</Box>
        </Stack>
      )
    },
  },
  {
    field: 'offerName',
    headerName: 'Bet',
    headerAlign: 'left',
    sortable: false,
    flex: .2,
  },
  {
    field: 'line',
    headerName: 'Line',
    headerAlign: 'left',
    flex: .1,
    sortable: false,
    renderCell(params: GridRenderCellParams<IShopOutcome>) {
      const { line, label } = params.row;
      if(!line) {
        return '-';
      }
      return `${label?.charAt(0)?.toUpperCase()} ${line}`
    },
  },
  {
    field: 'oddsAmerican',
    headerName: 'Odds',
    flex: .1,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
  },
  {
    field: 'sev',
    headerName: 'OS Rating',
    flex: .16,
    headerAlign: 'center',
    align: 'center',
    headerClassName: 'osrating',
    sortable: true,
    valueFormatter(params) {
      if (params.value) {
        if ((Number(params.value) * 10000).toFixed(0) === '0') {
          return '<1'
        }
        return (Number(params.value) * 10000).toFixed(0)
      }
      return '-';
    },
    renderCell(params) {
      const { formattedValue } = params;
      return (
        <Stack justifyContent='center' alignItems='center' width='calc(100% + .62px)' height='100%' fontWeight={700} style={{ backgroundColor: alpha("#009F52", (formattedValue * .05) > 1 ? 1 : formattedValue * .05 ), }}>
          {!isDisplayUnlocked ? <LockIcon /> : formattedValue}
        </Stack>
      )
    },
  },
  {
    field: 'expectedRoiPct',
    headerName: 'EV',
    headerAlign: 'center',
    flex: .1,
    sortable: true,
    align: 'center',
    type: 'number',
    headerClassName: isPickemTable ? 'osrating' : 'ev',
    valueGetter(params) {
      return params?.row?.expectedRoi || 0;
    },
    renderCell(params) {
      const lock = params.api.getSortedRowIds().indexOf(params.id) > 0 && !isDisplayUnlocked;
      const expectedRoiPct = params.row.expectedRoi ? (params.row.expectedRoi * 100).toFixed(1)+ '%' : '';

      return (
        <Stack justifyContent='center' alignItems='center' width='calc(100% + .62px)' height='100%' fontWeight={700} sx={{ backgroundColor: 'background.secondary', }}>
          <Stack justifyContent="center" alignItems="center" sx={{ height: '100%', width: '100%', backgroundColor: alpha('#00723B', parseInt(expectedRoiPct) * .05) }}>
            {lock ? <LockIcon /> : (expectedRoiPct ? expectedRoiPct : '-')}
          </Stack>
        </Stack>
      )
    },
    cellClassName: (params: GridCellParams<IShopOutcome>) => {
      let cellClasses = 'osratingCell';
      return cellClasses;
    },
  },
  {
    field: 'expectedWinPct',
    headerName: 'xWIN',
    headerAlign: 'center',
    flex: .1,
    align: 'center',
    sortable: true,
    type: 'number',
    ...(isPickemTable && {
      headerClassName: 'ev',
    }),
    valueGetter(params) {
      return parseInt(params.value)
    },
    renderCell(params) {
      const lock = params.api.getSortedRowIds().indexOf(params.id) > 0 && !isDisplayUnlocked;

      if (lock) {
        return <LockIcon />
      }
      if (isNaN(params.value)) {
        return '-';
      }
      if (!isPickemTable) {
        return params.value + '%';
      }

      return (
        <Stack justifyContent='center' alignItems='center' width='calc(100% + .62px)' height='100%' fontWeight={700} sx={{ backgroundColor: 'background.secondary', }}>
          <Stack justifyContent="center" alignItems="center" sx={{ height: '100%', width: '100%', backgroundColor: alpha('#00723B', params.value / 200) }}>
            {lock ? <LockIcon /> : (params.value ? params.value + '%' : '-')}
          </Stack>
        </Stack>
      )
    },
    cellClassName: (params: GridCellParams<IShopOutcome>) => {
      let cellClasses = 'evCell';
      return cellClasses;
    },
  },
  {
    field: 'trueOdds',
    headerName: 'True Odds',
    headerAlign: 'center',
    flex: .1,
    align: 'center',
    sortable: false,
    renderCell(params) {
      if (!isDisplayUnlocked) {
        return <LockIcon />
      }
      return params.value;
    },
  },
  {
    field: '',
    headerName: 'Add Leg',
    headerAlign: 'center',
    width: 120,
    align: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IShopOutcome>) => {
      const type = getType(params.row);
      const colorDictionary = {
        'Add': '#00954D',
        'Swap': '#4395FF',
        'Remove': '#EE707E',
      };

      const iconDictionary = {
        'Add': !isDisplayUnlocked ? <LockIcon fontSize="small" /> : <AddCircleIcon fontSize="small" />,
        'Swap': !isDisplayUnlocked ? <LockIcon fontSize="small" /> : <SwapHorizontalCircleIcon fontSize="small" />,
        'Remove': !isDisplayUnlocked ? <LockIcon fontSize="small" /> : <RemoveCircleIcon fontSize="small" />,
      }
      return (
        <Button disabled={!isDisplayUnlocked} variant="outlined" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', minWidth: '88px', color: colorDictionary[type], backgroundColor: alpha(colorDictionary[type], .1), fontSize: '10px', fontWeight: 500, border: `1px solid ${colorDictionary[type]}` }} endIcon={iconDictionary[type]} onClick={() => handleLegClick(params.row as IParlayOutcome)}>
          {type}
        </Button>
      )
    },
  },
]);

export const additionalLegsColumnsMobile = (getType: (o: IShopOutcome) => 'Add' | 'Swap' | 'Remove', handleLegClick: (o: IParlayOutcome) => void, isDisplayUnlocked: boolean, isPickEmTable?: boolean): GridColDef[] => ([
  {
    field: 'sev',
    headerName: 'OS Rating',
    headerAlign: 'center',
    align: 'center',
    headerClassName: 'osrating',
    sortable: true,
    cellClassName: 'osratingCell',
    valueFormatter(params) {
      if (params.value) {
        if ((Number(params.value) * 10000).toFixed(0) === '0') {
          return '<1'
        }
        return (Number(params.value) * 10000).toFixed(0)
      }
      return '-';
    },
  },
  {
    field: 'expectedRoiPct',
    headerName: 'EV',
    headerAlign: 'center',
    flex: .1,
    sortable: true,
    align: 'center',
    valueGetter(params) {
      return params.row.expectedRoi;
    },
  },
  {
    field: 'expectedWinPct',
    headerName: 'xWIN',
    headerAlign: 'center',
    flex: .1,
    align: 'center',
    sortable: true,
    renderCell(params) {
      if (!isDisplayUnlocked) {
        return <LockIcon />
      }
      return params.value;
    },
  },
  {
    field: 'oddsAmerican',
    headerName: 'Odds',
    flex: .1,
    headerAlign: 'center',
    align: 'center',
    sortable: true,
  },
  {
    field: '',
    headerName: '',
    flex: .2,
    headerAlign: 'left',
    renderCell(params: GridRenderCellParams<IShopOutcome>) {
      const type = getType(params.row);
      const { sev, expectedRoi, expectedWinPct, evAge } = params.row;
      const osRating = (Number(sev) * 10000);

      const expectedRoiPct = expectedRoi ? (expectedRoi * 100).toFixed(1)+ '%' : '';
      return (
        <Row
          isUserPremium={isDisplayUnlocked}
          parlayOutcome={params.row}
          type={type}
          onClick={(o: IParlayOutcome | IShopOutcome) => handleLegClick(o as IParlayOutcome)}
          contrastCardBackground={false}
          dataPoints={
            isPickEmTable ? [
              {
                header: 'EV',
                data: expectedRoiPct || '-',
                highlight: parseInt(expectedRoiPct || '0') * .05,
              },
              {
                header: 'xWin',
                data: expectedWinPct || '-',
                highlight: parseInt(expectedWinPct || '0') / 200,
              },
              {
                header: 'EV Age',
                data: evAgeLabel(evAge),
              },
            ] : [
            {
              header: 'OS Rating',
              data: sev ? osRating.toFixed(0) : '-',
              highlight: (osRating * .05) > 1 ? 1 : osRating * .05,
            },
            {
              header: 'EV',
              data: expectedRoiPct || '-',
              highlight: parseInt(expectedRoiPct || '0') * .05,
            },
            {
              header: 'xWin',
              data: expectedWinPct,
            },
            {
              header: 'EV Age',
              data: evAgeLabel(evAge),
            },
          ]}
        />
      )
    },
  },

]);